/**
 * OverviewTabContent component
 *
 * Workspace Overview with "Command Docket" aesthetic - editorial legal meets mission control.
 * Reorganized layout for better visual flow:
 * - Top: QUICK ACCESS (primary actions)
 * - Middle: CASE STATUS (horizontal info bar)
 * - Bottom: YOUR TEAMS (left) | RECENT ACTIVITY (right)
 *
 * Features editorial-style section dividers for clear information separation.
 *
 * NOTE: Only Teams and SharePoint work in B2B guest context.
 * Exchange, OneDrive, and Office apps always open in home tenant context
 * regardless of URL parameters - this is a fundamental Microsoft platform limitation.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { makeStyles, shorthands, mergeClasses } from '@fluentui/react-components';
import {
  Calendar20Regular,
  Person20Regular,
  Shield20Regular,
} from '@fluentui/react-icons';
import { TeamsIcon } from '../icons/TeamsIcon';
import { SharePointIcon } from '../icons/SharePointIcon';
import { TreeFileBrowser } from './TreeFileBrowser';
import { CollaborationSection } from './Collaboration/CollaborationSection';
import {
  buildTeamsUrl,
  buildSharePointUrl,
  type WorkspaceContext,
} from '../../utils/workspaceAppUrls';
import type { WorkspaceDetail, WorkspacePhase } from '../../types/workspace';

// ============================================================================
// STYLES - Command Docket Aesthetic with Editorial Separators
// ============================================================================

const useStyles = makeStyles({
  // Keyframe animations
  '@keyframes fadeInUp': {
    from: {
      opacity: '0',
      transform: 'translateY(12px)',
    },
    to: {
      opacity: '1',
      transform: 'translateY(0)',
    },
  },

  // Main container - vertical flow
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    minHeight: 0,
  },

  // ==================== SECTION STYLING ====================
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    animationName: 'fadeInUp',
    animationDuration: '0.4s',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    animationFillMode: 'both',
  },

  sectionHeader: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.45)',
  },

  // ==================== EDITORIAL DIVIDER ====================
  // Clean horizontal rule with centered label - legal document style
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.margin('36px', '0', '32px', '0'),
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 100%)',
  },

  dividerLineRight: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 100%)',
  },

  dividerLabel: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.3)',
    flexShrink: 0,
  },

  // Simple divider without label
  simpleDivider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.08) 15%, rgba(0,0,0,0.08) 85%, transparent 100%)',
    ...shorthands.margin('28px', '0', '24px', '0'),
  },

  // ==================== TOP ROW: CASE INFO + QUICK ACCESS ====================
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '24px',
    alignItems: 'start',
  },

  // ==================== QUICK ACCESS SECTION ====================
  quickAccessSection: {
    animationDelay: '0s',
  },

  quickAccessGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
  },

  quickAccessTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('20px', '16px'),
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: '100px',

    '&:hover': {
      ...shorthands.borderColor('rgba(0, 0, 0, 0.15)'),
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)',
    },
  },

  quickAccessTileDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,

    '&:hover': {
      ...shorthands.borderColor('rgba(0, 0, 0, 0.08)'),
      boxShadow: 'none',
      transform: 'none',
    },
  },

  quickAccessIcon: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('12px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  quickAccessLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.75)',
    textAlign: 'center',
  },

  // ==================== CASE STATUS SECTION ====================
  caseStatusSection: {
    animationDelay: '0s',
    flex: 1,
  },

  caseStatusCard: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('20px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  statusRow: {
    display: 'flex',
    gap: '32px',
  },

  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '120px',
  },

  statusDivider: {
    height: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },

  statusLabel: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.4)',
  },

  statusValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },

  statusValueIcon: {
    color: 'rgba(0, 0, 0, 0.35)',
    fontSize: '16px',
  },

  // Phase badge styling
  phaseBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('6px'),
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },

  phaseIntake: {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    color: '#4F46E5',
  },
  phaseDiscovery: {
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
    color: '#2563EB',
  },
  phaseAnalysis: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    color: '#D97706',
  },
  phaseRemediation: {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    color: '#059669',
  },
  phaseClosing: {
    backgroundColor: 'rgba(107, 114, 128, 0.12)',
    color: '#4B5563',
  },
  phaseClosed: {
    backgroundColor: 'rgba(107, 114, 128, 0.08)',
    color: '#6B7280',
  },

  // ==================== BOTTOM THREE-COLUMN SECTION ====================
  bottomGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '24px',
  },

  // ==================== YOUR TEAMS SECTION ====================
  teamsSection: {
    animationDelay: '0.1s',
  },

  teamsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  teamCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('14px', '16px'),
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',

    '&:hover': {
      ...shorthands.borderColor('rgba(0, 0, 0, 0.15)'),
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      transform: 'translateX(4px)',
    },
  },

  teamIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('10px'),
    backgroundColor: 'rgba(95, 95, 210, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#5f5fd2',
  },

  teamContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  teamName: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.9)',
  },

  teamMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '12px',
    color: 'rgba(0, 0, 0, 0.5)',
  },

  teamMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  unreadBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '22px',
    height: '22px',
    ...shorthands.borderRadius('11px'),
    backgroundColor: '#0078D4',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600',
    ...shorthands.padding('0', '6px'),
  },

  teamArrow: {
    color: 'rgba(0, 0, 0, 0.25)',
    transitionProperty: 'transform, color',
    transitionDuration: '0.15s',
  },

  // ==================== YOUR SITES SECTION ====================
  sitesSection: {
    animationDelay: '0.12s',
  },

  sitesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  siteCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('14px', '16px'),
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',

    '&:hover': {
      ...shorthands.borderColor('rgba(0, 0, 0, 0.15)'),
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      transform: 'translateX(4px)',
    },
  },

  siteIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('10px'),
    backgroundColor: 'rgba(3, 131, 135, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#038387',
  },

  siteContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  siteName: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'rgba(0, 0, 0, 0.9)',
  },

  siteMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '12px',
    color: 'rgba(0, 0, 0, 0.5)',
  },

  siteArrow: {
    color: 'rgba(0, 0, 0, 0.25)',
    transitionProperty: 'transform, color',
    transitionDuration: '0.15s',
  },

  // ==================== RECENT ACTIVITY SECTION ====================
  activitySection: {
    animationDelay: '0.15s',
  },

  activityCard: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('16px', '20px'),
  },

  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0, 0, 0, 0.05)'),

    '&:last-child': {
      ...shorthands.borderBottom('none'),
      paddingBottom: '0',
    },

    '&:first-child': {
      paddingTop: '0',
    },
  },

  activityDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    flexShrink: 0,
    marginTop: '6px',
  },

  activityDotRecent: {
    backgroundColor: '#0078D4',
  },

  activityContent: {
    flex: 1,
    minWidth: 0,
  },

  activityText: {
    fontSize: '13px',
    color: 'rgba(0, 0, 0, 0.85)',
    lineHeight: '1.4',
  },

  activityTime: {
    fontSize: '11px',
    color: 'rgba(0, 0, 0, 0.4)',
    marginTop: '3px',
  },

  // Document items in activity list
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('10px', '0'),
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0, 0, 0, 0.05)'),
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',

    '&:last-child': {
      ...shorthands.borderBottom('none'),
      paddingBottom: '0',
    },

    '&:first-child': {
      paddingTop: '0',
    },

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },

  documentIcon: {
    width: '28px',
    height: '28px',
    ...shorthands.borderRadius('6px'),
    backgroundColor: 'rgba(3, 131, 135, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#038387',
  },

  documentContent: {
    flex: 1,
    minWidth: 0,
  },

  documentName: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(0, 0, 0, 0.85)',
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },

  documentMeta: {
    fontSize: '11px',
    color: 'rgba(0, 0, 0, 0.45)',
    marginTop: '2px',
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('32px'),
    color: 'rgba(0, 0, 0, 0.4)',
    fontSize: '14px',
  },

});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract tenant name from domain for SharePoint URL construction.
 */
function getTenantNameFromDomain(domain: string): string {
  const withoutSuffix = domain.replace('.onmicrosoft.com', '');
  return withoutSuffix.split('.')[0];
}

/**
 * Get phase badge style class
 */
function getPhaseStyle(phase: WorkspacePhase, styles: ReturnType<typeof useStyles>): string {
  const phaseStyles: Record<WorkspacePhase, string> = {
    intake: styles.phaseIntake,
    discovery: styles.phaseDiscovery,
    analysis: styles.phaseAnalysis,
    remediation: styles.phaseRemediation,
    closing: styles.phaseClosing,
    closed: styles.phaseClosed,
  };
  return phaseStyles[phase];
}

/**
 * Format phase for display
 */
function formatPhase(phase: WorkspacePhase): string {
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

// ============================================================================
// TYPES
// ============================================================================

interface QuickAccessItem {
  id: string;
  label: string;
  icon: ReactNode;
  iconBackground: string;
  href: string;
  disabled?: boolean;
}

interface OverviewTabContentProps {
  workspaceId: string;
  workspaceName: string;
  domain: string;
  /** Workspace data for case status and teams */
  workspace?: WorkspaceDetail;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SectionDividerProps {
  label?: string;
}

/**
 * Editorial-style section divider with optional centered label
 */
function SectionDivider({ label }: SectionDividerProps) {
  const styles = useStyles();

  if (!label) {
    return <div className={styles.simpleDivider} />;
  }

  return (
    <div className={styles.dividerContainer}>
      <div className={styles.dividerLine} />
      <span className={styles.dividerLabel}>{label}</span>
      <div className={mergeClasses(styles.dividerLine, styles.dividerLineRight)} />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function OverviewTabContent({
  workspaceId,
  workspaceName,
  domain,
  workspace,
}: OverviewTabContentProps) {
  const styles = useStyles();

  const workspaceContext: WorkspaceContext = useMemo(
    () => ({
      workspaceId,
      workspaceName,
      domain,
    }),
    [workspaceId, workspaceName, domain]
  );

  // Quick access items - compact tiles
  const quickAccessItems: QuickAccessItem[] = useMemo(() => [
    {
      id: 'teams',
      label: 'Teams',
      icon: <TeamsIcon size={28} />,
      iconBackground: 'rgba(95, 95, 210, 0.1)',
      href: buildTeamsUrl(workspaceContext),
    },
    {
      id: 'sharepoint',
      label: 'Documents',
      icon: <SharePointIcon size={28} />,
      iconBackground: 'rgba(3, 131, 135, 0.1)',
      href: buildSharePointUrl(
        `https://${getTenantNameFromDomain(domain)}.sharepoint.com`,
        workspaceContext
      ),
    },
  ], [workspaceContext, domain]);

  // Use workspace data or defaults for case status
  const casePhase = workspace?.phase ?? 'discovery';
  const createdAt = workspace?.createdAt ?? new Date().toISOString();
  const userRole = workspace?.role ?? 'Legal Counsel';
  const accessType = 'Full Access';
  const caseLead = workspace?.leadName ?? 'Sarah Chen';

  return (
    <div className={styles.container}>
      {/* ══════════════════ TOP ROW: CASE INFO + QUICK ACCESS ══════════════════ */}
      <div className={styles.topRow}>
        {/* CASE INFORMATION - Left side, two rows */}
        <div className={mergeClasses(styles.section, styles.caseStatusSection)}>
          <span className={styles.sectionHeader}>CASE INFORMATION</span>
          <div className={styles.caseStatusCard}>
            {/* Row 1: Phase, Created, Case Lead */}
            <div className={styles.statusRow}>
              <div className={styles.statusCell}>
                <span className={styles.statusLabel}>Phase</span>
                <span className={mergeClasses(styles.phaseBadge, getPhaseStyle(casePhase, styles))}>
                  {formatPhase(casePhase)}
                </span>
              </div>
              <div className={styles.statusCell}>
                <span className={styles.statusLabel}>Created</span>
                <span className={styles.statusValue}>
                  <Calendar20Regular className={styles.statusValueIcon} />
                  {new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className={styles.statusCell}>
                <span className={styles.statusLabel}>Case Lead</span>
                <span className={styles.statusValue}>
                  <Person20Regular className={styles.statusValueIcon} />
                  {caseLead}
                </span>
              </div>
            </div>

            <div className={styles.statusDivider} />

            {/* Row 2: Your Role, Access */}
            <div className={styles.statusRow}>
              <div className={styles.statusCell}>
                <span className={styles.statusLabel}>Your Role</span>
                <span className={styles.statusValue}>
                  <Person20Regular className={styles.statusValueIcon} />
                  {userRole}
                </span>
              </div>
              <div className={styles.statusCell}>
                <span className={styles.statusLabel}>Access Level</span>
                <span className={styles.statusValue}>
                  <Shield20Regular className={styles.statusValueIcon} />
                  {accessType}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* QUICK ACCESS - Right side, small tiles */}
        <div className={mergeClasses(styles.section, styles.quickAccessSection)}>
          <span className={styles.sectionHeader}>QUICK ACCESS</span>
          <div className={styles.quickAccessGrid}>
            {quickAccessItems.map((item) =>
              item.disabled ? (
                <div
                  key={item.id}
                  className={mergeClasses(styles.quickAccessTile, styles.quickAccessTileDisabled)}
                  data-testid={`quick-access-${item.id}`}
                >
                  <span
                    className={styles.quickAccessIcon}
                    style={{ backgroundColor: item.iconBackground }}
                  >
                    {item.icon}
                  </span>
                  <span className={styles.quickAccessLabel}>{item.label}</span>
                </div>
              ) : (
                <a
                  key={item.id}
                  className={styles.quickAccessTile}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`quick-access-${item.id}`}
                >
                  <span
                    className={styles.quickAccessIcon}
                    style={{ backgroundColor: item.iconBackground }}
                  >
                    {item.icon}
                  </span>
                  <span className={styles.quickAccessLabel}>{item.label}</span>
                </a>
              )
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════ COLLABORATION SECTION ══════════════════ */}
      <CollaborationSection tenantId={workspaceId} />

      {/* ─────────────── Divider ─────────────── */}
      <SectionDivider label="Documents" />

      {/* ══════════════════ DOCUMENTS SECTION ══════════════════ */}
      <TreeFileBrowser tenantId={workspaceId} />
    </div>
  );
}
